import { ORGANISATION_QUESTION_METADATA } from '../NominationFormPage/NominationFormQuestion/hook/useOrganisationField';
import { formatKey } from '@percent/grants-nomination-form/utils/formKey';
import { NominationForm } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';
import transform from 'lodash/transform';

const cleanEmptyValues = (object: Record<string, unknown>) =>
  transform(object, (accumulator: Record<string, unknown>, value, key) => {
    const cleanedValue = (() => {
      // If value is an object, recursively clean it
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return cleanEmptyValues(value as Record<string, unknown>);
      }

      // Filter out empty arrays (e.g. multi-select with no selections)
      if (Array.isArray(value) && value.length === 0) {
        return undefined;
      }

      // Convert empty strings to undefined, otherwise keep original value
      // This is required as API throws error when field is an empty string
      return value === '' ? undefined : value;
    })();

    accumulator[key] = cleanedValue;
  });

export const mapFormPayload = (
  arrayFieldsKeys: string[],
  values: Record<string, unknown>,
  nominationFormData: NominationForm,
) => {
  const form = cloneDeep(values);

  if (form[ORGANISATION_QUESTION_METADATA.ADD_ORGANISATION_ENABLED]) {
    const organisationQuestion = nominationFormData.pages
      .flatMap((page) => page.questions)
      .find((question) => question?.type === 'organisation_search');

    // we don't want to send organisation id if add organisation is enabled
    if (organisationQuestion) {
      unset(form, organisationQuestion?.key);
    }
  }

  // remove metadata used by the form
  delete form[ORGANISATION_QUESTION_METADATA.ORGANISATION];
  delete form[ORGANISATION_QUESTION_METADATA.QUERY];
  delete form[ORGANISATION_QUESTION_METADATA.ADD_ORGANISATION_ENABLED];

  const allQuestions = nominationFormData.pages.flatMap((page) => page.questions);

  const resolveQuestionId = (arrayFieldKey: string): string | undefined => {
    for (const question of allQuestions) {
      if (!question) continue;
      if (formatKey(question.key) === arrayFieldKey) return question.id;
      if (question.type === 'money') {
        if (formatKey(question.amountKey) === arrayFieldKey) return question.amountQuestionId;
        if (question.currencyKey && formatKey(question.currencyKey) === arrayFieldKey) {
          return question.currencyQuestionId;
        }
      }
    }
    return undefined;
  };

  arrayFieldsKeys.forEach((arrayFieldKey) => {
    const valueKey = formatKey(arrayFieldKey);
    const answer = get(values, valueKey);

    if (!answer) {
      return;
    }

    const [arrayFieldPath] = arrayFieldKey.split('[]');

    const newAnswer = {
      questionId: resolveQuestionId(valueKey),
      answer,
    };

    const currentValue = get(form, arrayFieldPath);
    set(form, arrayFieldPath, Array.isArray(currentValue) ? [...currentValue, newAnswer] : [newAnswer]);
  });

  return cleanEmptyValues(form);
};
